import { AlertTriangle, Loader2 } from 'lucide-react';

export interface StrategyCanvasUnlinkImpact {
  objectives: string[];
  goals: string[];
  outcomeDrivers: string[];
  /** Other strategies that stay linked to this canvas after removal */
  otherLinkedStrategyNames: string[];
}

const LIST_CAP = 8;

function ImpactList({ label, items }: { label: string; items: string[] }) {
  if (items.length === 0) return null;
  const shown = items.slice(0, LIST_CAP);
  const more = items.length - shown.length;
  return (
    <div className="mt-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-ter)]">
        {label}
      </p>
      <ul className="mt-1 space-y-0.5 text-xs text-[var(--text-pri)] pl-3 list-disc">
        {shown.map((name, i) => (
          <li key={`${name}-${i}`}>{name}</li>
        ))}
      </ul>
      {more > 0 && (
        <p className="mt-1 text-[10px] text-[var(--text-ter)] pl-3">
          +{more} more
        </p>
      )}
    </div>
  );
}

/**
 * Confirms removing a strategy from the canvas and summarizes what disappears from this view.
 */
export function StrategyCanvasUnlinkConfirmModal({
  open,
  loading,
  strategyName,
  impact,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  loading: boolean;
  strategyName: string;
  impact: StrategyCanvasUnlinkImpact | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;

  const hasLists =
    impact &&
    (impact.objectives.length > 0 ||
      impact.goals.length > 0 ||
      impact.outcomeDrivers.length > 0);

  return (
    <div
      className="fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/50 dark:bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="canvas-unlink-title"
    >
      <div className="w-full max-w-md border border-[var(--rule)] bg-[var(--bg-raised)] shadow-xl">
        <div className="flex items-start gap-3 px-5 pt-5 pb-2">
          <div className="flex-shrink-0 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/40 p-2">
            <AlertTriangle className="h-5 w-5 text-[var(--mustard)] dark:text-[var(--mustard)]" />
          </div>
          <div className="min-w-0 flex-1">
            <h2
              id="canvas-unlink-title"
              className="text-base font-semibold text-[var(--text-pri)]"
            >
              Remove this strategy from the canvas?
            </h2>
            <p className="mt-2 text-sm text-[var(--text-sec)] leading-relaxed">
              You are about to detach <span className="font-medium text-[var(--text-pri)]">{strategyName}</span> from
              this strategy canvas. The strategy itself stays in your workspace—it is not deleted.
            </p>
          </div>
        </div>

        <div className="px-5 pb-4 max-h-[min(50vh,320px)] overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 py-6 justify-center text-sm text-[var(--text-ter)]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Checking related items…
            </div>
          ) : (
            <>
              <p className="text-sm text-[var(--text-sec)] leading-relaxed">
                What changes here: objectives, goals, and outcome drivers that only show up on this canvas because of this
                strategy will drop out of this view. They remain saved in your workspace; you can still open them from
                their own pages or link this strategy again later.
              </p>

              {hasLists ? (
                <div className="mt-3 border border-[var(--mustard)] dark:border-[var(--mustard)]/40 bg-[var(--mustard-dim)]/50 dark:bg-[var(--mustard)]/20 px-3 py-2">
                  <p className="text-xs font-medium text-[var(--mustard)] dark:text-[var(--mustard)]">
                    You will no longer see these on this canvas after you remove the strategy:
                  </p>
                  <ImpactList label="Objectives" items={impact!.objectives} />
                  <ImpactList label="Goals" items={impact!.goals} />
                  <ImpactList label="Outcome drivers" items={impact!.outcomeDrivers} />
                </div>
              ) : (
                <p className="mt-3 text-xs text-[var(--text-ter)] italic">
                  No objectives, goals, or outcome drivers in this workspace are scoped only to this strategy in a way
                  that would disappear from the canvas summary—aside from the strategy card itself.
                </p>
              )}

              {impact && impact.otherLinkedStrategyNames.length > 0 && (
                <p className="mt-3 text-xs text-[var(--text-sec)] leading-relaxed">
                  Other strategies stay linked to this canvas:{' '}
                  <span className="font-medium text-[var(--text-pri)]">
                    {impact.otherLinkedStrategyNames.join(' · ')}
                  </span>
                  .
                </p>
              )}

              {impact && impact.otherLinkedStrategyNames.length === 0 && (
                <p className="mt-3 text-xs text-[var(--text-sec)]">
                  After this removal, no strategies will be linked to this canvas until you add one again.
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-5 py-4 border-t border-[var(--rule)] bg-[var(--surface)]/50 dark:bg-[var(--bg-raised)]/30">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy || loading}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy || loading}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-[var(--coral)] hover:bg-[var(--coral)] dark:bg-[var(--coral)] dark:hover:bg-[var(--coral)] disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Remove from canvas
          </button>
        </div>
      </div>
    </div>
  );
}
